import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { CREATE_PROFILE_ANSWER_MUTATION } from "@/api/mutations/createProfileAnswer";
import {
  CreateProfileAnswerMutation,
  CreateProfileAnswerMutationVariables,
} from "@/__generated__/types";
import { MutationErrorHandler } from "@/util/mutationUtils";

export interface UseCreateProfileAnswerParams {
  onCompleted?: (data: CreateProfileAnswerMutation) => void;
  onError?: (error: unknown) => void;
}

export interface CreateProfileAnswerResult {
  success: boolean;
  errors: {
    message: string;
    title: string;
  }[];
}

export interface UseCreateProfileAnswerReturn {
  loading: boolean;
  error: unknown;
}

export interface CreateProfileAnswerInput {
  userId: string;
  profileQuestionId: string;
  answer: string;
}

export const useCreateProfileAnswer = ({
  onCompleted,
  onError,
}: UseCreateProfileAnswerParams = {}): [
  (input: CreateProfileAnswerInput) => Promise<CreateProfileAnswerResult>,
  UseCreateProfileAnswerReturn,
] => {
  const [createProfileAnswerMutation, { loading, error }] = useMutation<
    CreateProfileAnswerMutation,
    CreateProfileAnswerMutationVariables
  >(CREATE_PROFILE_ANSWER_MUTATION, {
    onCompleted,
    onError,
    refetchQueries: ["ProfileQuestions", "ProfileAnswers"],
    awaitRefetchQueries: true,
  });

  const createProfileAnswer = useCallback(
    async (
      input: CreateProfileAnswerInput,
    ): Promise<CreateProfileAnswerResult> => {
      try {
        const result = await createProfileAnswerMutation({
          variables: {
            userId: input.userId,
            profileQuestionId: input.profileQuestionId,
            answer: input.answer,
          },
        });

        const errorHandler = MutationErrorHandler.fromResult(
          result,
          result.errors,
        );
        return errorHandler.toResult();
      } catch (error) {
        return {
          success: false,
          errors: [
            {
              message: error instanceof Error ? error.message : "Unknown error",
              title: "Mutation Error",
            },
          ],
        };
      }
    },
    [createProfileAnswerMutation],
  );

  return [createProfileAnswer, { loading, error }];
};
